import React from "react";
import { EmptyState, Icon, LogoMark } from "./registry";
import { AppErrors } from "./apperrors";   // Ops & Health → App errors (the app's own crashes, migration #38)
/* ============================================================
   Fence Works — Help chat
   • HelpBubble   — the floating "?" on every app screen. One thread per
                    signed-in user; what they type goes to the founder's
                    inbox (email + optional text) via /support/*, and
                    replies show up right here (polled) + by email.
   • SupportInbox — the owner's side (route "support", admin-only): every
                    thread, newest first, reply + close.
   • useSupportStatus — one light poller shared by the bubble, the sidebar
                    badge and the inbox route gate.
   Local/demo mode (no VFP_API_BASE) → the bubble still opens, but sending
   hands off to a mailto: so nobody ever hits a dead end.
   ============================================================ */
const SUPPORT_EMAIL = "rob@tryfenceworks.com";
const supportApi = (): any => (window as any).VFP_DB && (window as any).VFP_DB.support;

function fmtTime(at: any) {
  const d = new Date(at); if (isNaN(d.getTime())) return "";
  const now = new Date(); const sameDay = d.toDateString() === now.toDateString();
  const t = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return sameDay ? t : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " + t;
}
const ago = (at: any) => {
  const m = Math.floor((Date.now() - new Date(at).getTime()) / 60000);
  return m < 1 ? "now" : m < 60 ? m + "m" : m < 1440 ? Math.floor(m / 60) + "h" : Math.floor(m / 1440) + "d";
};

/* ---- owner-side status for the sidebar (fed by the bubble's poller) ---- */
let lastAdmin: any = { admin: false, inboxUnread: 0, verticals: ["fence"] };   // + the services this workspace runs (the switcher, #43)
function useSupportAdmin() {
  const [v, setV] = React.useState<any>(lastAdmin);
  React.useEffect(() => {
    const h = () => setV(lastAdmin);
    window.addEventListener("fw:support-status", h); return () => window.removeEventListener("fw:support-status", h);
  }, []);
  return v;
}

/* ---- shared status poller: { live, admin, ownerName, thread, messages, inboxUnread, refresh } ---- */
function useSupportStatus(auth: any, open: boolean) {
  const api = supportApi();
  const live = !!(api && api.enabled && api.enabled());
  const [st, setSt] = React.useState<any>({ admin: false, ownerName: "Robby", thread: null, messages: [], inboxUnread: 0, loaded: false });
  const refresh = React.useCallback(() => {
    if (!live || !(auth && auth.user)) return Promise.resolve();
    return api.me().then((d: any) => {
      if (!d) return;
      setSt({ admin: !!d.admin, ownerName: (d.owner && d.owner.name) || "Robby", thread: d.thread, messages: d.messages || [], inboxUnread: d.inboxUnread || 0, loaded: true, setup: d.setup !== false });
      // fan the owner-side bits out to the sidebar (Support Inbox item + badge)
      lastAdmin = { admin: !!d.admin, inboxUnread: d.inboxUnread || 0, verticals: Array.isArray(d.verticals) && d.verticals.length ? d.verticals : ["fence"] };
      window.dispatchEvent(new Event("fw:support-status"));
    });
  }, [live, auth && auth.user]);
  React.useEffect(() => {
    if (!live || !(auth && auth.user)) return;
    let dead = false;
    const tick = () => { if (!dead) refresh(); };
    tick();
    // a reply can land any time — poll faster while the panel is open
    const t = setInterval(tick, open ? 12000 : 60000);
    window.addEventListener("fw:support", tick);
    return () => { dead = true; clearInterval(t); window.removeEventListener("fw:support", tick); };
  }, [live, auth && auth.user, open, refresh]);
  return { live, ...st, refresh };
}

/* ============================================================
   HelpBubble — floating launcher + panel
   ============================================================ */
function HelpBubble({ auth, route, go }: any) {
  const [open, setOpen] = React.useState<any>(() => {
    // email reply link (app.fenceworks.ai/?help=1) opens the chat straight away
    try { const p = new URLSearchParams(window.location.search); if (p.get("help")) { window.history.replaceState({}, "", window.location.pathname); return true; } } catch (e) {}
    return false;
  });
  const [text, setText] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [sentLocal, setSentLocal] = React.useState<any[]>([]);   // optimistic rows until the poll catches up
  const bodyRef = React.useRef<any>(null);
  const inputRef = React.useRef<any>(null);
  const s = useSupportStatus(auth, open);
  const api = supportApi();

  // sidebar "Help" + guide links open the bubble
  React.useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("fw:help-open", h); return () => window.removeEventListener("fw:help-open", h);
  }, []);
  // opening = read; clear the badge + focus the composer
  React.useEffect(() => {
    if (!open) return;
    if (s.live && s.thread && s.thread.unread && api) api.read().then(() => window.dispatchEvent(new Event("fw:support")));
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
  }, [open, s.thread && s.thread.unread]);
  // Esc closes
  React.useEffect(() => {
    if (!open) return;
    const k = (e: any) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, [open]);

  const serverMsgs = s.messages || [];
  const msgs = [...serverMsgs, ...sentLocal.filter((l) => !serverMsgs.some((m: any) => m.body === l.body && m.role === "user" && Math.abs(new Date(m.created_at).getTime() - l.at) < 120000))];
  React.useEffect(() => { const el = bodyRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs.length, open]);

  async function send() {
    const body = text.trim(); if (!body || busy) return;
    setErr("");
    if (!s.live) {
      // demo / offline: hand off to email so the message still reaches a person
      const who = (auth && auth.user && auth.user.email) ? `\n\n— ${auth.user.email}` : "";
      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Fence Works help")}&body=${encodeURIComponent(body + who)}`;
      setText(""); return;
    }
    setBusy(true);
    try {
      setSentLocal((l) => [...l, { role: "user", body, created_at: new Date().toISOString(), at: Date.now() }]);
      setText("");
      await api.send(body, route || "");
      window.dispatchEvent(new Event("fw:support"));
    } catch (e: any) {
      setErr(String((e && e.message) || e));
      setSentLocal((l) => l.filter((x) => x.body !== body));
      setText(body);
    } finally { setBusy(false); }
  }

  const unread = !!(s.thread && s.thread.unread);
  const ownerFirst = String(s.ownerName || "Robby").split(" ")[0];

  return (
    <>
      <button className={"help-fab" + (open ? " open" : "")} onClick={() => setOpen((o: any) => !o)} title={open ? "Close help" : "Help & support — chat with us"} aria-label="Help & support">
        {open ? <Icon name="x" size={20} /> : <Icon name="chat" size={22} />}
        {!open && unread && <span className="help-fab-dot" />}
      </button>

      {open && (
        <div className="help-panel" role="dialog" aria-label="Help & support">
          <div className="help-head">
            <div className="row gap-10" style={{ alignItems: "center", minWidth: 0 }}>
              <LogoMark size={28} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14.5 }}>Help &amp; support</div>
                <div className="muted" style={{ fontSize: 11.5 }}>
                  {s.live ? <>Goes straight to <b style={{ color: "var(--fg-2)" }}>{ownerFirst}</b>, the founder · replies here + by email</> : <>Local mode · messages go out by email</>}
                </div>
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} title="Close"><Icon name="x" size={15} /></button>
          </div>

          <div className="help-quick">
            <button className="help-qbtn" onClick={() => { setOpen(false); go && go("guide"); }}><Icon name="book" size={14} />Open the guide</button>
            <a className="help-qbtn" href={`mailto:${SUPPORT_EMAIL}`}><Icon name="mail" size={14} />Email us</a>
          </div>

          <div className="help-body" ref={bodyRef}>
            {msgs.length === 0
              ? (
                <div className="help-welcome">
                  <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4 }}>👋 Stuck on something? Ask here.</div>
                  <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                    Questions, bugs, feature requests — anything. {s.live ? `${ownerFirst} reads every message and usually replies within a few hours.` : "Sending opens your email app with the message ready to go."}
                  </div>
                </div>
              )
              : msgs.map((m: any, i: number) => (
                <div key={m.id || "l" + i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "84%" }}>
                  <div className={"hub-msg " + (m.role === "user" ? "me" : "company")} style={{ maxWidth: "100%", whiteSpace: "pre-wrap" }}>{m.body}</div>
                  <div className="faint" style={{ fontSize: 10.5, margin: "2px 4px 0", textAlign: m.role === "user" ? "right" : "left" }}>
                    {m.role === "user" ? "You" : ownerFirst} · {fmtTime(m.created_at)}
                  </div>
                </div>
              ))}
            {s.live && s.loaded && s.setup === false && <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>Help chat tables aren't on the server yet — messages fall back to email.</p>}
          </div>

          {err && <p style={{ color: "var(--red)", fontSize: 12, margin: "0 14px 6px" }}>{err}</p>}
          <div className="help-compose">
            <textarea ref={inputRef} className="textarea" rows={2} placeholder={s.live ? `Message ${ownerFirst}…` : "Write your message…"} value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={send} title="Send (Enter)"><Icon name="send" size={15} /></button>
          </div>
          <div className="faint help-foot">Enter to send · Shift+Enter for a new line{auth && auth.user && auth.user.email ? ` · replies also go to ${auth.user.email}` : ""}</div>
        </div>
      )}
    </>
  );
}

/* ============================================================
   SupportInbox — the owner's side (route "support")
   ============================================================ */
/* ============================================================
   FW-030/031 — the operator's panel.
   ------------------------------------------------------------
   Two questions this product couldn't answer at 11pm:

     what is actually deployed?          → no build identity anywhere
     which migrations has this DB had?   → no record at all

   The second is the dangerous one, because every server module degrades to a
   fallback when its table is missing — on purpose, so a forgotten migration
   doesn't take the product down. Which means a half-migrated database looks
   like a working one: cost caps not atomic, the checkout race open, SMS
   silently refusing, and the only signal a console warning nobody is reading.

   And two queues nobody could see: a dead-lettered send (four failed retries,
   the customer's reminder never went out) and a payment collected but never
   folded onto its quote. Both were rows you had to already know to go looking
   for. Retry and re-apply are here too — a queue you can only stare at isn't
   recovery.
   ============================================================ */
const opsApi = (): any => (window as any).VFP_DB && (window as any).VFP_DB.ops;

function Alerts({ alerts }: any) {
  if (!alerts || !alerts.length) {
    return <p className="muted" style={{ fontSize: 12.5, margin: 0, color: "var(--green)" }}>✓ Nothing needs attention — schema complete, no stranded money, no dead letters.</p>;
  }
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {alerts.map((a: any, i: number) => (
        <div key={i} className="row gap-8" style={{ alignItems: "baseline", fontSize: 12.5 }}>
          <span style={{ color: a.level === "error" ? "var(--red)" : "var(--amber)", fontWeight: 700, flexShrink: 0 }}>{a.level === "error" ? "✗" : "!"}</span>
          <span><b>{a.what}</b> — {a.detail}{a.impact ? <span className="muted"> · {a.impact}</span> : null}</span>
        </div>
      ))}
    </div>
  );
}

/* Contractor-website hosting, read from the outside by the service
   (server/src/hostingcheck.js): the env, the sites app, the preview host, DNS
   on the sites zone, HTTPS end to end, Cloudflare for SaaS. A red row names
   the deploy/DEPLOY.md §11 step that fixes it — those are dashboard clicks no
   code can do, and until this panel nothing on the box could say whether they
   had been done. */
function HostingRows({ hosting }: any) {
  if (!hosting) return null;
  const rows: any[] = hosting.rows || [];
  const mark = (ok: any) => ok === true ? ["✓", "var(--green)"] : ok === false ? ["✗", "var(--red)"] : ["–", "var(--fg-faint)"];
  return (
    <div>
      <div className="row gap-8" style={{ alignItems: "baseline", marginBottom: 6, flexWrap: "wrap" }}>
        <div className="kicker">Websites hosting</div>
        <span className="mono" style={{ fontSize: 11.5, color: hosting.error ? "var(--amber)" : hosting.ready ? "var(--green)" : "var(--amber)" }}>
          {hosting.error ? "check failed: " + hosting.error : hosting.ready ? `ready · ${hosting.root}` : `${(hosting.failing || []).length} of ${rows.filter((r) => r.required).length} steps not done`}
        </span>
      </div>
      {!hosting.error && (
        <div style={{ display: "grid", gap: 3 }}>
          {rows.map((r: any) => { const [sym, color] = mark(r.ok); return (
            <div key={r.id} className="row gap-8" style={{ fontSize: 11.5, alignItems: "baseline" }}>
              <span style={{ flexShrink: 0, width: 14, color, fontWeight: 700 }}>{sym}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ fontWeight: r.ok === false ? 700 : 500 }}>{r.label}</span>
                {r.detail ? <span className="muted"> · {r.detail}</span> : null}
                {r.ok === false && r.step ? <span style={{ color: "var(--amber)" }}> · fix: DEPLOY.md {r.step}</span> : null}
              </span>
            </div>
          ); })}
        </div>
      )}
      {!hosting.error && !hosting.ready && <p className="muted" style={{ fontSize: 11.5, margin: "6px 0 0" }}>Until every required row is green, a contractor's own domain can't go live; the Website screen's Domain tab tells them to wait. Each probe is a real request from this box, so Re-check after each dashboard step.</p>}
    </div>
  );
}

/* a tiny line chart for a minute series: the bar is the bucket's value, the
   title is its time and number; the last 12 buckets are brighter */
function MiniSeries({ series, pick, max: maxIn, unit = "", label = "" }: any) {
  const pts: { at: string; v: number | null }[] = (series || []).map((d: any) => ({ at: d.at, v: pick(d) }));
  const vals = pts.map((p) => (p.v == null ? 0 : Number(p.v)));
  const max = Math.max(1, maxIn || 0, ...vals);
  const W = 2, H = 28, n = pts.length;
  if (!n) return <span className="faint" style={{ fontSize: 11 }}>no samples yet</span>;
  return (
    <svg width={n * W} height={H} viewBox={`0 0 ${n * W} ${H}`} role="img" aria-label={label} style={{ display: "block", maxWidth: "100%" }} preserveAspectRatio="none">
      <line x1={0} x2={n * W} y1={H - 0.5} y2={H - 0.5} stroke="var(--line)" strokeWidth={1} />
      {pts.map((p, i) => {
        const h = p.v == null ? 0 : Math.max(1, Math.round((Number(p.v) / max) * (H - 2)));
        const when = new Date(p.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
        return <g key={i}><title>{`${when}: ${p.v == null ? "—" : p.v}${unit}`}</title><rect x={i * W} y={H - 1 - h} width={W} height={h} fill={i >= n - 12 ? "var(--teal)" : "var(--fg-faint)"} /></g>;
      })}
    </svg>
  );
}

/* The box's own numbers (server/src/capacity.js; migration #49): a plain
   verdict first, then now vs the range's peaks, the series, and PM2. The point
   is one sentence Rob can act on before anyone notices the site is slow. */
function CapacityRows({ initial, api }: any) {
  const [cap, setCap] = React.useState<any>(initial || null);
  const [hours, setHours] = React.useState<number>(24);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setCap(initial || null); }, [initial]);
  const pickRange = (h: number) => {
    setHours(h); setBusy(true);
    api.capacity(h).then((c: any) => setCap(c)).catch(() => {}).finally(() => setBusy(false));
  };
  if (!cap) return null;
  if (cap.error) return <div><div className="kicker" style={{ marginBottom: 6 }}>Capacity</div><p className="muted" style={{ fontSize: 12, margin: 0, color: "var(--amber)" }}>check failed: {cap.error}</p></div>;
  if (cap.setup === false) return <div><div className="kicker" style={{ marginBottom: 6 }}>Capacity</div><p className="muted" style={{ fontSize: 12, margin: 0 }}>Run migration #49 ({cap.migration}) — until then the box's load isn't recorded.</p></div>;
  const L = cap.latest || {}, P = cap.peaks || {}, V = cap.verdict || {};
  const color = V.level === "scale" ? "var(--red)" : V.level === "watch" ? "var(--amber)" : V.level === "ok" ? "var(--green)" : "var(--fg-faint)";
  const n = (v: any, d = 0) => (v == null || !Number.isFinite(Number(v)) ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: d }));
  const tiles: [string, string, string][] = [
    ["CPU", `${n(L.cpu_pct)}%`, `peak ${n(P.cpu10)}% (10-min) · ${L.cpus || "?"} cores · load ${n(L.load1, 2)}`],
    ["Memory", `${n(L.mem_used_pct)}%`, `${n(L.mem_avail_mb)} MB available of ${n(L.mem_total_mb)} · peak ${n(P.mem)}%`],
    ["Disk", `${n(L.disk_used_pct)}%`, `${n(L.disk_free_gb, 1)} GB free`],
    ["API requests", `${n(L.req_per_min)}/min`, `peak ${n(P.req)}/min · 5xx ${n(V.errs)} of ${n(V.reqs)} (${n(V.errRate, 1)}%)`],
    ["API latency", `${n(L.p95_ms)} ms p95`, `p50 ${n(L.p50_ms)} · peak p95 ${n(P.p95)} ms · loop p99 ${n(L.loop_p99_ms, 1)} ms`],
    ["nginx", L.nginx_active == null ? "—" : `${n(L.nginx_active)} conn`, L.nginx_active == null ? "add deploy/nginx/status.conf" : `${n(L.nginx_req_per_min)}/min all sites · peak ${n(P.nginxReq)}/min`],
    ["Sites app", L.sites_ms == null ? "down" : `${n(L.sites_ms)} ms`, "health round trip from this box"],
  ];
  const pm2: any[] = Array.isArray(cap.pm2) ? cap.pm2 : [];
  return (
    <div>
      <div className="row between" style={{ alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <div className="row gap-8" style={{ alignItems: "baseline", flexWrap: "wrap" }}>
          <div className="kicker">Capacity</div>
          <span className="mono" style={{ fontSize: 11.5, color }}>{V.level === "scale" ? "resize now" : V.level === "watch" ? "getting close" : V.level === "ok" ? "plenty of room" : "no samples"}{cap.stale && cap.latest ? " · stale" : ""} · {cap.host}</span>
        </div>
        <div className="row gap-8">
          {[24, 168].map((h) => <button key={h} className={"btn btn-ghost btn-sm" + (hours === h ? " on" : "")} style={{ padding: "2px 8px", fontWeight: hours === h ? 700 : 500 }} disabled={busy} onClick={() => pickRange(h)}>{h === 24 ? "24 h" : "7 days"}</button>)}
        </div>
      </div>
      <p style={{ fontSize: 12.5, margin: "0 0 10px", color }}>{V.text}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        {tiles.map(([k, v, sub]) => (
          <div key={k} style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px" }}>
            <div className="faint" style={{ fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase" }}>{k}</div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em" }}>{v}</div>
            <div className="faint" style={{ fontSize: 11 }}>{sub}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 10 }}>
        <div><div className="faint" style={{ fontSize: 11, marginBottom: 2 }}>CPU % ({hours === 168 ? "7 days" : "24 h"})</div><MiniSeries series={cap.series} pick={(d: any) => d.cpu} max={100} unit="%" label="CPU" /></div>
        <div><div className="faint" style={{ fontSize: 11, marginBottom: 2 }}>Requests per bucket</div><MiniSeries series={cap.series} pick={(d: any) => (d.nginxReq != null ? d.nginxReq : d.req)} label="requests" /></div>
        <div><div className="faint" style={{ fontSize: 11, marginBottom: 2 }}>API p95 ms</div><MiniSeries series={cap.series} pick={(d: any) => d.p95} unit=" ms" label="p95 latency" /></div>
        <div><div className="faint" style={{ fontSize: 11, marginBottom: 2 }}>Memory used %</div><MiniSeries series={cap.series} pick={(d: any) => d.mem} max={100} unit="%" label="memory" /></div>
      </div>
      {pm2.length > 0 && (
        <table className="table" style={{ width: "100%", fontSize: 11.5, marginTop: 10 }}>
          <thead><tr><th>PM2 process</th><th>Status</th><th>CPU</th><th>Memory</th><th>Restarts</th><th>Up</th></tr></thead>
          <tbody>{pm2.map((p: any) => (
            <tr key={p.name}><td>{p.name}</td><td style={{ color: p.status === "online" ? "var(--green)" : "var(--red)", fontWeight: 700 }}>{p.status}</td><td>{n(p.cpu, 1)}%</td><td>{n(p.memMb)} MB</td><td>{n(p.restarts)}</td><td>{p.uptimeMin == null ? "—" : p.uptimeMin >= 1440 ? `${Math.floor(p.uptimeMin / 1440)}d` : p.uptimeMin >= 60 ? `${Math.floor(p.uptimeMin / 60)}h` : `${p.uptimeMin}m`}</td></tr>
          ))}</tbody>
        </table>
      )}
      {(cap.pm2Trouble || []).length > 0 && <p style={{ fontSize: 12, margin: "6px 0 0", color: "var(--red)" }}>{cap.pm2Trouble.join(" · ")}</p>}
      <p className="muted" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Resize when the 10-minute CPU peak passes 80% or memory available drops under 10%; "getting close" is 60% and 20%. How to resize, and when to split the sites app onto its own box instead: docs/OPS.md §9.</p>
    </div>
  );
}

/* `page` = rendered as its own screen (FW Admin → Ops & Health): everything
   open from the start, and an honest empty state instead of vanishing when
   the caller isn't the platform owner. */
function OpsPanel({ page }: any = {}) {
  const api = opsApi();
  const [diag, setDiag] = React.useState<any>(null);
  const [rec, setRec] = React.useState<any>(null);
  const [open, setOpen] = React.useState(!!page);
  const [busy, setBusy] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const [note, setNote] = React.useState<any>("");
  const [denied, setDenied] = React.useState<any>(false);

  const load = React.useCallback(() => {
    if (!api || !api.enabled()) return;
    setErr("");
    api.diagnostics().then(setDiag).catch((e: any) => { if (/403|forbidden/i.test(String(e.message || e))) setDenied(true); else setErr(String(e.message || e)); });
    api.recovery().then(setRec).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (!diag && page) {
    if (denied) return <EmptyState icon="lock" title="Owner only" sub="Ops & Health is limited to the platform owner's account (SUPPORT_OWNER_EMAILS, verified)." />;
    if (err) return <p className="muted" style={{ color: "var(--amber)" }}>{err}</p>;
    return <p className="muted" style={{ fontSize: 13 }}>Checking the service…</p>;
  }

  async function act(kind: string, id: any) {
    setBusy(kind + ":" + id); setErr(""); setNote("");
    try {
      if (kind === "retry") { await api.retryDeadLetter(id); setNote("Queued — the worker picks it up within a minute, and re-checks consent and quiet hours before it goes."); }
      else if (kind === "dismiss") { await api.dismissDeadLetter(id); setNote("Dismissed."); }
      else if (kind === "reapply") { const r = await api.reapplyPayment(id); setNote(r.detail || "Re-folded onto the quote."); }
      else if (kind === "qboRetry") { await api.retryQboEvent(id); setNote("Queued — the worker picks it up within a minute. A payment already on the quote is never added twice."); }
      else if (kind === "qboDismiss") { await api.dismissQboEvent(id); setNote("Dismissed."); }
      load();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(null); }
  }

  if (!diag) return null;              // not the platform owner, or no live service
  const b = diag.build || {};
  const counts = {
    dead: rec && rec.dead ? rec.dead.count : 0,
    stuck: rec && rec.stuck ? rec.stuck.count : 0,
    failed: rec && rec.failed ? rec.failed.count : 0,
    unapplied: rec && rec.unapplied ? rec.unapplied.count : 0,
    qboDead: rec && rec.qboDead ? rec.qboDead.count : 0,
  };
  const bad = (diag.alerts || []).filter((a: any) => a.level === "error").length;

  return (
    <div className="card pad" style={{ margin: "14px 0", borderColor: bad ? "var(--red)" : counts.dead + counts.stuck ? "var(--amber)" : undefined }}>
      <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <div className="row gap-8" style={{ alignItems: "baseline", flexWrap: "wrap" }}>
          <span className="kicker">Service</span>
          <span className="mono" style={{ fontSize: 11.5 }}>
            {b.unknown ? <span style={{ color: "var(--amber)" }}>build unknown</span> : b.shortSha}
            {b.branch ? " · " + b.branch : ""} · node {b.node} · up {Math.floor((b.uptimeSecs || 0) / 3600)}h
          </span>
        </div>
        <div className="row gap-8">
          <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={load}><Icon name="bolt" size={12} />Re-check</button>
          {!page && <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={() => setOpen(!open)}>{open ? "Hide" : "Details"}</button>}
        </div>
      </div>
      <Alerts alerts={diag.alerts} />
      {note && <p className="muted" style={{ fontSize: 12, margin: "8px 0 0", color: "var(--teal)" }}>{note}</p>}
      {err && <p className="muted" style={{ fontSize: 12, margin: "8px 0 0", color: "var(--amber)" }}>{err}</p>}

      {open && (
        <div style={{ borderTop: "1px solid var(--line)", marginTop: 12, paddingTop: 12, display: "grid", gap: 14 }}>
          <AppErrors />

          <HostingRows hosting={diag.hosting} />

          <CapacityRows initial={diag.capacity} api={api} />

          <div>
            <div className="kicker" style={{ marginBottom: 6 }}>Migrations</div>
            {!diag.schema || !diag.schema.recordAvailable
              ? <p className="muted" style={{ fontSize: 12, margin: "0 0 6px" }}>No migration record on this database — run <code>migrate_2026-09-17_ops_evidence.sql</code>. The probe below still works.</p>
              : null}
            <div style={{ display: "grid", gap: 3 }}>
              {((diag.schema && diag.schema.migrations) || []).map((m: any) => (
                <div key={m.name} className="row gap-8" style={{ fontSize: 11.5, alignItems: "baseline" }}>
                  <span style={{ flexShrink: 0, width: 14, color: m.status === "ok" ? "var(--green)" : m.status === "missing" ? "var(--red)" : "var(--amber)", fontWeight: 700 }}>
                    {m.status === "ok" ? "✓" : m.status === "missing" ? "✗" : "?"}
                  </span>
                  <span className="mono" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{m.name.replace(/^migrate_/, "")}</span>
                  <span className="faint" style={{ fontSize: 10.5, flexShrink: 0 }}>
                    {m.status === "ok" ? (m.appliedAt ? new Date(m.appliedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "applied")
                      : m.status === "applied-unrecorded" ? "applied, unrecorded"
                      : m.status === "missing" ? "NOT APPLIED" : "can't tell"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <OpsQueue title="Messages that gave up" q={rec && rec.dead} count={counts.dead}
            empty="Nothing dead-lettered. Every queued message either went out or was deliberately stopped."
            row={(r: any) => (
              <>
                <div style={{ fontSize: 12, marginBottom: 3 }}><b>{r.automation || r.sequence_id}</b> <span className="faint">· {r.channel} · {r.attempts} attempts</span></div>
                <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>{r.last_error}</div>
                {r.body && <div className="faint" style={{ fontSize: 11, marginBottom: 6, fontStyle: "italic" }}>“{r.body}”</div>}
                <div className="row gap-8">
                  <button className="btn btn-primary btn-sm" disabled={busy === "retry:" + r.id} onClick={() => act("retry", r.id)}>{busy === "retry:" + r.id ? "Queueing…" : "Retry"}</button>
                  <button className="btn btn-ghost btn-sm" disabled={busy === "dismiss:" + r.id} onClick={() => act("dismiss", r.id)}>Don't send</button>
                </div>
              </>
            )} />

          <OpsQueue title="Money collected but not on the quote" q={rec && rec.unapplied} count={counts.unapplied}
            empty="No stranded payments. Every collected payment is folded onto its quote."
            row={(r: any) => (
              <>
                <div style={{ fontSize: 12, marginBottom: 3 }}><b>{window.VFP.money(r.amount)}</b> <span className="faint">· {r.kind} · quote {r.quote_id}</span></div>
                <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>Taken {r.paid_at ? new Date(r.paid_at).toLocaleString("en-US") : "—"} · session {String(r.session_id || "").slice(0, 20)}</div>
                <button className="btn btn-primary btn-sm" disabled={busy === "reapply:" + (r.session_id || r.id)} onClick={() => act("reapply", r.session_id || r.id)}>{busy === "reapply:" + (r.session_id || r.id) ? "Applying…" : "Re-apply to the quote"}</button>
              </>
            )} />

          <OpsQueue title="QuickBooks changes that gave up" q={rec && rec.qboDead} count={counts.qboDead}
            empty="Nothing stuck. Every QuickBooks payment the webhook or the hourly sweep found was applied or deliberately skipped."
            row={(r: any) => (
              <>
                <div style={{ fontSize: 12, marginBottom: 3 }}><b>{r.entity === "_unreadable" ? "A delivery this server couldn't read" : r.entity + " " + r.operation + " #" + r.entity_id}</b> <span className="faint">· {r.workspace_id || "company " + (r.realm_id || "?")} · {r.source} · {r.attempts} attempts</span></div>
                <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>{r.last_error}</div>
                <div className="row gap-8">
                  {r.entity !== "_unreadable" && <button className="btn btn-primary btn-sm" disabled={busy === "qboRetry:" + r.id} onClick={() => act("qboRetry", r.id)}>{busy === "qboRetry:" + r.id ? "Queueing…" : "Retry"}</button>}
                  <button className="btn btn-ghost btn-sm" disabled={busy === "qboDismiss:" + r.id} onClick={() => act("qboDismiss", r.id)}>Dismiss</button>
                </div>
              </>
            )} />

          <OpsQueue title="Sends stuck mid-flight" q={rec && rec.stuck} count={counts.stuck}
            empty="No stuck sends."
            row={(r: any) => (
              <div style={{ fontSize: 12 }}>
                <b>{r.sequence_id}</b> <span className="faint">· {r.channel} · claimed {r.lease_until ? new Date(r.lease_until).toLocaleString("en-US") : "—"} and never settled</span>
              </div>
            )} />

          <OpsQueue title="Failed & disputed payments" q={rec && rec.failed} count={counts.failed}
            empty="No failed or disputed payments."
            row={(r: any) => (
              <div style={{ fontSize: 12 }}>
                <b>{window.VFP.money(r.amount)}</b> <span className="faint">· {r.status} · {r.kind} · quote {r.quote_id} · {r.paid_at ? new Date(r.paid_at).toLocaleDateString("en-US") : "—"}</span>
              </div>
            )} />
        </div>
      )}
    </div>
  );
}

/* One queue. `available: false` means the view isn't there — which is not the
   same answer as "the queue is empty", and saying so is the point. */
function OpsQueue({ title, q, count, empty, row }: any) {
  if (!q) return null;
  return (
    <div>
      <div className="row between" style={{ marginBottom: 6 }}>
        <div className="kicker">{title}</div>
        {count > 0 && <span className="chip" style={{ fontSize: 10.5, color: "var(--amber)", fontWeight: 700 }}>{count}</span>}
      </div>
      {!q.available
        ? <p className="muted" style={{ fontSize: 12, margin: 0, color: "var(--amber)" }}>Can't see this queue{q.note ? " — " + q.note : ""}. That is not the same as it being empty.</p>
        : !q.rows.length
          ? <p className="muted" style={{ fontSize: 12, margin: 0 }}>{empty}</p>
          : <div style={{ display: "grid", gap: 8 }}>
              {q.rows.map((r: any) => <div key={r.id} style={{ borderTop: "1px solid var(--line)", paddingTop: 8 }}>{row(r)}</div>)}
            </div>}
    </div>
  );
}

/* FW Admin → Ops & Health: the operator's own page. What this box is running,
   whether the database is whole, and the four recovery queues. */
function OpsPage() {
  const api = opsApi();
  const live = !!(api && api.enabled && api.enabled());
  return (
    <div className="page fade-up" style={{ maxWidth: 920 }}>
      <div style={{ marginBottom: 4 }}>
        <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Ops &amp; Health</h1>
        <p className="muted" style={{ margin: "4px 0 0" }}>What is deployed, which migrations this database has really had, and what broke that nobody was looking at.</p>
      </div>
      {live
        ? <OpsPanel page />
        : <div className="card pad" style={{ marginTop: 14, textAlign: "center" }}><EmptyState icon="activity" title="Needs the live service" sub="Diagnostics and the recovery queues live on the automation service (app.fenceworks.ai)." /></div>}
    </div>
  );
}

function SupportInbox({ auth, params, go }: any) {
  const api = supportApi();
  const live = !!(api && api.enabled && api.enabled());
  // the deletion-request email used to link here (/support?deletions=1) —
  // that queue is its own page now
  React.useEffect(() => { if (params && params.deletions && go) go("deletions"); }, [params && params.deletions]);
  const [threads, setThreads] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<any>(true);
  const [denied, setDenied] = React.useState<any>(false);
  const [filter, setFilter] = React.useState<any>("open");
  const [activeId, setActiveId] = React.useState<any>((params && params.thread) || null);
  const [active, setActive] = React.useState<any>(null);
  const [msgs, setMsgs] = React.useState<any[]>([]);
  const [text, setText] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [note, setNote] = React.useState<any>("");
  const bodyRef = React.useRef<any>(null);

  const refreshList = React.useCallback(() => {
    if (!live) { setLoading(false); return; }
    api.inbox().then((d: any) => { setThreads(d.threads || []); setLoading(false); setDenied(false); })
      .catch((e: any) => { setLoading(false); if (/team|forbidden|403/i.test(String(e.message || e))) setDenied(true); else setErr(String(e.message || e)); });
  }, [live]);
  React.useEffect(() => { refreshList(); const t = setInterval(refreshList, 10000); return () => clearInterval(t); }, [refreshList]);

  const refreshThread = React.useCallback(() => {
    if (!live || !activeId) return;
    api.thread(activeId).then((d: any) => { setActive(d.thread); setMsgs(d.messages || []); window.dispatchEvent(new Event("fw:support")); }).catch((e: any) => setErr(String(e.message || e)));
  }, [live, activeId]);
  React.useEffect(() => { setMsgs([]); refreshThread(); const t = setInterval(refreshThread, 6000); return () => clearInterval(t); }, [refreshThread]);
  React.useEffect(() => { const el = bodyRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs.length, activeId]);
  // default to the newest thread once the list arrives
  React.useEffect(() => { if (!activeId && threads.length) setActiveId(threads[0].id); }, [threads.length]);

  async function reply() {
    const body = text.trim(); if (!body || !activeId || busy) return;
    setBusy(true); setErr(""); setNote("");
    try {
      setMsgs((m) => [...m, { role: "admin", body, created_at: new Date().toISOString() }]);
      setText("");
      const r = await api.reply(activeId, body);
      setNote(r && r.emailed ? "Sent — and emailed to them." : "Sent in-app (no email address on file).");
      refreshThread(); refreshList();
    } catch (e: any) { setErr(String(e.message || e)); setText(body); }
    finally { setBusy(false); }
  }
  async function setStatus(status: string) {
    if (!activeId) return;
    try { await api.setStatus(activeId, status); refreshThread(); refreshList(); } catch (e: any) { setErr(String(e.message || e)); }
  }

  const rows = threads.filter((t) => filter === "all" ? true : filter === "unread" ? t.unread : t.status === filter);
  const unreadCount = threads.filter((t) => t.unread).length;

  if (!live) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="chat" title="Support inbox needs the live service" sub="Help-chat threads live on app.fenceworks.ai (the automation service). In local mode the bubble hands messages off to email." /></div>
    </div>
  );
  if (denied) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="lock" title="Fence Works team only" sub="This inbox shows every customer's help chat. Add your email to SUPPORT_OWNER_EMAILS on the server to unlock it." />
        <button className="btn btn-primary" onClick={() => go("dashboard")}>Back to dashboard</button></div>
    </div>
  );

  return (
    <div className="page fade-up" style={{ maxWidth: 1100 }}>
      <div className="row between" style={{ flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
        <div>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Support Inbox</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Every help-chat message from every account. Replies show up in their bubble and go out by email.</p>
        </div>
        {unreadCount > 0 && <span className="chip" style={{ color: "var(--red)", fontWeight: 700 }}>{unreadCount} waiting</span>}
      </div>
      <div className="cat-tabs" style={{ margin: "14px 0 16px" }}>
        {[["open", "Open"], ["unread", "Unread"], ["closed", "Closed"], ["all", "All"]].map(([id, label]) => (
          <button key={id} className={"filter-chip" + (filter === id ? " on" : "")} onClick={() => setFilter(id)}>{label}</button>
        ))}
      </div>

      <div className="msg-layout">
        <aside className="msg-list card" style={{ padding: 0 }}>
          {loading
            ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: 24 }}>Loading…</p>
            : rows.length === 0
              ? <EmptyState icon="chat" title="Inbox zero" sub={filter === "open" ? "No open conversations. Nice." : "Nothing here."} />
              : rows.map((t: any) => (
                <button key={t.id} className={"msg-thread" + (t.id === activeId ? " on" : "")} onClick={() => setActiveId(t.id)}>
                  <div className="cust-ava" style={{ width: 34, height: 34, fontSize: 14, position: "relative" }}>
                    {(t.user_name || t.user_email || "?").charAt(0).toUpperCase()}
                    {t.unread && <span style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: 99, background: "var(--red)", border: "2px solid var(--surface)" }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row between" style={{ gap: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.user_name || t.user_email || "User"}</div>
                      <span className="faint" style={{ fontSize: 10.5, flexShrink: 0 }}>{t.last_at ? ago(t.last_at) : ""}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {t.company_name ? t.company_name + " · " : ""}{t.preview ? (t.preview.role === "admin" ? "You: " : "") + t.preview.body : t.user_email}
                    </div>
                  </div>
                </button>
              ))}
        </aside>

        <section className="msg-panel card" style={{ padding: 0 }}>
          {active ? (
            <>
              <div className="msg-head">
                <div className="row gap-10" style={{ alignItems: "center", minWidth: 0 }}>
                  <div className="cust-ava" style={{ width: 32, height: 32, fontSize: 13 }}>{(active.user_name || active.user_email || "?").charAt(0).toUpperCase()}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5 }}>{active.user_name || active.user_email}{active.status === "closed" && <span className="chip" style={{ marginLeft: 8, fontSize: 10 }}>Closed</span>}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {[active.company_name, active.user_email, active.lastPage ? "last on " + active.lastPage : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
                <div className="row gap-8">
                  {active.user_email && <a className="btn btn-ghost btn-sm" href={"mailto:" + active.user_email} title="Email them instead"><Icon name="mail" size={13} /></a>}
                  {active.status === "closed"
                    ? <button className="btn btn-outline btn-sm" onClick={() => setStatus("open")}>Reopen</button>
                    : <button className="btn btn-ghost btn-sm" onClick={() => setStatus("closed")}><Icon name="check" size={13} />Close</button>}
                </div>
              </div>
              <div className="msg-body" ref={bodyRef}>
                {msgs.map((m: any, i: number) => (
                  <div key={m.id || "l" + i} style={{ alignSelf: m.role === "admin" ? "flex-end" : "flex-start", maxWidth: "78%" }}>
                    <div className={"hub-msg " + (m.role === "admin" ? "me" : "company")} style={{ maxWidth: "100%", whiteSpace: "pre-wrap" }}>{m.body}</div>
                    <div className="faint" style={{ fontSize: 10.5, margin: "2px 4px 0", textAlign: m.role === "admin" ? "right" : "left" }}>
                      {m.role === "admin" ? "You" : (active.user_name || "Them")}{m.page && m.role === "user" ? " · from " + m.page : ""} · {fmtTime(m.created_at)}
                    </div>
                  </div>
                ))}
              </div>
              {err && <p style={{ color: "var(--red)", fontSize: 12, padding: "0 14px" }}>{err}</p>}
              {note && !err && <p className="faint" style={{ fontSize: 11.5, padding: "0 14px" }}>{note}</p>}
              <div className="msg-compose">
                <textarea className="textarea" rows={2} placeholder={"Reply to " + (active.user_name || active.user_email || "them") + "…"} value={text}
                  onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); reply(); } }} style={{ flex: 1 }} />
                <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={reply}><Icon name="send" size={15} /></button>
              </div>
            </>
          ) : <div className="msg-empty" style={{ padding: 60 }}><EmptyState icon="chat" title="Pick a conversation" sub="Threads from the help bubble show up on the left as they come in." /></div>}
        </section>
      </div>
    </div>
  );
}

Object.assign(window, { HelpBubble, SupportInbox, OpsPage, useSupportStatus, useSupportAdmin });

export { HelpBubble, SupportInbox, OpsPage, useSupportStatus, useSupportAdmin };
